import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  CheckCircle2, AlertTriangle, XCircle, Clock, RefreshCw,
  ChevronRight, Eye, Send, ArrowRight, User, FileText, RotateCw,
  GripHorizontal, ChevronUp, ChevronDown,
} from 'lucide-react';

const BUCKET_CONFIG = {
  ready: { label: 'Ready', color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200', icon: CheckCircle2 },
  needs_review: { label: 'Needs Review', color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200', icon: Eye },
  needs_fix: { label: 'Needs Fix', color: 'text-red-600', bg: 'bg-red-50 border-red-200', icon: XCircle },
  in_progress: { label: 'In Progress', color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200', icon: Clock },
  returned_to_provider: { label: 'Returned to Provider', color: 'text-gray-600', bg: 'bg-gray-50 border-gray-200', icon: Send },
  approved: { label: 'Approved', color: 'text-emerald-700', bg: 'bg-emerald-100 border-emerald-300', icon: CheckCircle2 },
};

function CodingStatusChip({ state }) {
  const cfg = BUCKET_CONFIG[state] || { label: state, color: 'text-gray-500', bg: 'bg-gray-50' };
  return <span className={`inline-flex items-center text-[10px] font-medium rounded px-1.5 py-0.5 border ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>;
}

// ═══════════════════════════════════════
// DASHBOARD TAB
// ═══════════════════════════════════════
function DashboardTab({ dashboard, onSelectBucket }) {
  if (!dashboard) return <div className="p-4 text-sm text-muted-foreground">Loading dashboard...</div>;

  return (
    <div className="space-y-4" data-testid="coding-dashboard">
      {/* Summary */}
      <div className="flex gap-3">
        <Card className="flex-1"><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-blue-600">{dashboard.total_active}</p>
          <p className="text-[10px] text-muted-foreground uppercase">Active</p>
        </CardContent></Card>
        <Card className="flex-1"><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-emerald-600">{dashboard.total_approved}</p>
          <p className="text-[10px] text-muted-foreground uppercase">Approved</p>
        </CardContent></Card>
      </div>

      {/* Bucket cards — clickable */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="bucket-cards">
        {Object.entries(dashboard.buckets || {}).filter(([s]) => s !== 'approved').map(([state, info]) => {
          const cfg = BUCKET_CONFIG[state] || {};
          const Icon = cfg.icon || Clock;
          return (
            <Card key={state} className={`cursor-pointer hover:ring-2 transition-all border ${cfg.bg}`}
                  onClick={() => onSelectBucket(state)} data-testid={`bucket-card-${state}`}>
              <CardContent className="p-4 text-center">
                <Icon className={`h-5 w-5 mx-auto mb-1 ${cfg.color}`} />
                <p className={`text-2xl font-bold ${cfg.color}`}>{info.count}</p>
                <p className="text-[10px] text-muted-foreground uppercase">{info.label}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════
// BUCKET TAB (list of charges in a state)
// ═══════════════════════════════════════
function BucketTab({ state, onSelectCharge, selectedId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/rcm/coding/bucket/${state}`);
      setItems(r.data.items || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [state]);

  useEffect(() => { load(); }, [load]);

  const cfg = BUCKET_CONFIG[state] || {};

  if (loading) return <div className="p-4 text-sm text-muted-foreground">Loading {cfg.label}...</div>;

  return (
    <div className="space-y-4" data-testid={`bucket-${state}`}>
      <Card>
        <CardHeader className="pb-1 pt-3 px-3 flex flex-row justify-between items-center">
          <CardTitle className="text-xs flex items-center gap-1.5">
            {cfg.icon && <cfg.icon className={`h-3 w-3 ${cfg.color}`} />}
            {cfg.label} ({items.length})
          </CardTitle>
          <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={load}>
            <RefreshCw className="h-3 w-3 mr-1" />Refresh
          </Button>
        </CardHeader>
        <CardContent className="px-3 pb-3">
          {items.length === 0 ? (
            <p className="text-xs text-muted-foreground py-4 text-center">No encounters in {cfg.label}</p>
          ) : (
            <table className="w-full text-xs" data-testid={`bucket-table-${state}`}>
              <thead><tr className="border-b text-left">
                <th className="py-1.5 font-medium text-muted-foreground">Patient</th>
                <th className="py-1.5 font-medium text-muted-foreground">Provider</th>
                <th className="py-1.5 font-medium text-muted-foreground">Primary CPT</th>
                <th className="py-1.5 font-medium text-muted-foreground">Visit Type</th>
                <th className="py-1.5 font-medium text-muted-foreground">Date</th>
                <th className="py-1.5 font-medium text-muted-foreground">Coding</th>
                <th className="py-1.5 font-medium text-muted-foreground">Charge</th>
                <th></th>
              </tr></thead>
              <tbody>{items.map((c) => (
                <tr key={c.id} className={`border-b cursor-pointer transition-colors ${c.id === selectedId ? 'bg-blue-50 ring-1 ring-blue-300' : 'hover:bg-muted/30'}`}
                    onClick={() => onSelectCharge(c.id)} data-testid={`charge-row-${c.id?.slice(0,8)}`}>
                  <td className="py-1.5 font-medium">{c._patient_name || '—'}</td>
                  <td className="py-1.5 text-muted-foreground">{c.provider_name || '—'}</td>
                  <td className="py-1.5 font-mono">{c.primary_code || '—'}</td>
                  <td className="py-1.5">{c.visit_type || '—'}</td>
                  <td className="py-1.5 text-muted-foreground">{c.date_of_service?.slice(0, 10) || '—'}</td>
                  <td className="py-1.5"><CodingStatusChip state={c.coding_state} /></td>
                  <td className="py-1.5"><Badge variant="outline" className="text-[9px]">{c.charge_status?.replace(/_/g, ' ')}</Badge></td>
                  <td className="py-1.5"><ChevronRight className="h-3 w-3 text-muted-foreground" /></td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════
// WORKBENCH PANEL — Split view (Slice 6A.3-B)
// Left: SOAP/facts/evidence (read-only). Right: editable code table.
// Edit state is LOCAL until Save Draft (Slice C).
// NO silent mutation. NO auto-coding.
// ═══════════════════════════════════════
function WorkbenchPanel({ chargeId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Local edit state — NOT persisted until explicit Save (Slice C)
  const [editCodes, setEditCodes] = useState([]);
  const [rationale, setRationale] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/rcm/coding/charges/${chargeId}/workbench`);
        setData(r.data);
        setEditCodes(r.data.current_codes || []);
        setRationale(r.data.coder_rationale || '');
      } catch(e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [chargeId]);

  // ── Code editing helpers (local state only) ──
  const updateCode = (idx, field, value) => {
    setEditCodes(prev => prev.map((c, i) => i === idx ? { ...c, [field]: value } : c));
    setDirty(true);
  };

  const addCode = () => {
    setEditCodes(prev => [...prev, { index: prev.length, cpt_code: '', type: 'procedure', modifiers: [], units: 1, charge_amount: 0, linked_diagnoses: [] }]);
    setDirty(true);
  };

  const removeCode = (idx) => {
    setEditCodes(prev => prev.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const addModifier = (idx, mod) => {
    if (!mod) return;
    setEditCodes(prev => prev.map((c, i) => i === idx ? { ...c, modifiers: [...(c.modifiers || []), mod] } : c));
    setDirty(true);
  };

  const removeModifier = (idx, modIdx) => {
    setEditCodes(prev => prev.map((c, i) => i === idx ? { ...c, modifiers: (c.modifiers || []).filter((_, mi) => mi !== modIdx) } : c));
    setDirty(true);
  };

  const toggleDiagnosis = (codeIdx, dxCode) => {
    setEditCodes(prev => prev.map((c, i) => {
      if (i !== codeIdx) return c;
      const linked = c.linked_diagnoses || [];
      return { ...c, linked_diagnoses: linked.includes(dxCode) ? linked.filter(d => d !== dxCode) : [...linked, dxCode] };
    }));
    setDirty(true);
  };

  // ── Save actions (Slice C) ──
  const [saving, setSaving] = useState(false);
  const [returnMsg, setReturnMsg] = useState('');
  const [flagReason, setFlagReason] = useState('');
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const [showFlagDialog, setShowFlagDialog] = useState(false);

  const saveDraft = async () => {
    setSaving(true);
    try {
      await api.post(`/rcm/coding/charges/${chargeId}/save-draft`, { codes: editCodes, rationale });
      setDirty(false);
    } catch(e) { alert(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const approveCoding = async () => {
    if (!rationale.trim()) { alert('Rationale is required for approval'); return; }
    if (editCodes.length === 0) { alert('At least one code is required'); return; }
    setSaving(true);
    try {
      await api.post(`/rcm/coding/charges/${chargeId}/approve`, { codes: editCodes, rationale });
      setDirty(false);
      onClose();
    } catch(e) { alert(e.response?.data?.detail || 'Approval failed'); }
    finally { setSaving(false); }
  };

  const flagForReview = async () => {
    setSaving(true);
    try {
      await api.post(`/rcm/coding/charges/${chargeId}/flag-review`, { codes: editCodes, rationale, reason: flagReason });
      setDirty(false); setShowFlagDialog(false); setFlagReason('');
      onClose();
    } catch(e) { alert(e.response?.data?.detail || 'Flag failed'); }
    finally { setSaving(false); }
  };

  const returnToProvider = async () => {
    if (!returnMsg.trim()) { alert('A message to the provider is required'); return; }
    setSaving(true);
    try {
      await api.post(`/rcm/coding/charges/${chargeId}/return-to-provider`, { codes: editCodes, rationale, message: returnMsg });
      setDirty(false); setShowReturnDialog(false); setReturnMsg('');
      onClose();
    } catch(e) { alert(e.response?.data?.detail || 'Return failed'); }
    finally { setSaving(false); }
  };

  const releaseToBilling = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/rcm/coding/charges/${chargeId}/release-to-billing`);
      alert(`Claim created: ${r.data?.claim?.id?.slice(0,12) || r.data?.claim?._id?.slice(0,12) || '?'}... — charge released to billing`);
      onClose();
    } catch(e) { alert(e.response?.data?.detail || 'Release failed'); }
    finally { setSaving(false); }
  };
  if (!data) return null;

  const soap = data.soap_sections || {};
  const diagnoses = data.encounter_diagnoses || [];

  return (
    <Card className="border-blue-300" data-testid="workbench-panel">
      <CardHeader className="pb-1 pt-3 px-4 flex flex-row justify-between items-center border-b">
        <div>
          <CardTitle className="text-sm flex items-center gap-2">
            Coder Workbench — <span className="font-mono text-xs text-muted-foreground">{chargeId.slice(0, 12)}...</span>
            <CodingStatusChip state={data.charge?.coding_state} />
            {dirty && <Badge variant="outline" className="text-[9px] text-amber-600 border-amber-300">Unsaved changes</Badge>}
          </CardTitle>
          <p className="text-[10px] text-muted-foreground">{data.patient_name} — {data.charge?.visit_type} — {data.charge?.date_of_service}</p>
        </div>
        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onClose} data-testid="close-workbench">Close</Button>
      </CardHeader>

      {/* SPLIT LAYOUT */}
      <div className="grid grid-cols-1 lg:grid-cols-2 divide-x" data-testid="workbench-split">
        {/* ═══ LEFT: Reference Panel (read-only) ═══ */}
        <div className="p-3 space-y-3 max-h-[600px] overflow-y-auto" data-testid="reference-panel">
          {/* SOAP Note */}
          {soap.hpi && (
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">HPI</p>
              <p className="text-xs leading-relaxed">{soap.hpi}</p>
            </div>
          )}
          {soap.ros && (
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">ROS</p>
              <p className="text-xs leading-relaxed">{soap.ros}</p>
            </div>
          )}
          {soap.exam && (
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Exam</p>
              <p className="text-xs leading-relaxed">{soap.exam}</p>
            </div>
          )}
          {soap.assessment && (
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Assessment</p>
              <p className="text-xs leading-relaxed">{soap.assessment}</p>
            </div>
          )}

          {/* Problems with ICD codes */}
          {data.problems?.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Problems / Diagnoses</p>
              <div className="space-y-1">
                {data.problems.map((p, i) => (
                  <div key={i} className="text-xs flex gap-2 items-start">
                    <span className="font-mono text-[10px] bg-blue-50 border border-blue-200 rounded px-1 shrink-0">{p.icd_code || '—'}</span>
                    <span>{p.problem_name?.split('(')[0]?.trim()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Evidence highlights */}
          {data.evidence_highlights?.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Evidence / Flags</p>
              {data.evidence_highlights.map((ev, i) => (
                <div key={i} className={`text-xs p-1.5 rounded mb-1 ${ev.type === 'validation_flag' ? 'bg-amber-50 border border-amber-200 text-amber-800' : 'bg-gray-50 border border-gray-200'}`}>
                  {ev.text}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ═══ RIGHT: Coding Engine (editable) ═══ */}
        <div className="p-3 space-y-3 max-h-[600px] overflow-y-auto" data-testid="coding-engine">
          {/* Editable Code Table */}
          <div>
            <div className="flex justify-between items-center mb-1.5">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Procedure Codes</p>
              <Button variant="outline" size="sm" className="h-5 text-[10px] px-2" onClick={addCode} data-testid="add-code-btn">+ Add Code</Button>
            </div>
            <div className="space-y-2">
              {editCodes.map((code, idx) => (
                <div key={idx} className="border rounded p-2 text-xs space-y-1.5 bg-white" data-testid={`code-row-${idx}`}>
                  <div className="flex gap-2 items-center">
                    {/* CPT input */}
                    <div className="flex-1">
                      <label className="text-[9px] text-muted-foreground">CPT/HCPCS</label>
                      <input type="text" className="w-full border rounded px-2 py-1 text-xs font-mono"
                        value={code.cpt_code} onChange={e => updateCode(idx, 'cpt_code', e.target.value)}
                        data-testid={`cpt-input-${idx}`} />
                    </div>
                    {/* Units */}
                    <div className="w-16">
                      <label className="text-[9px] text-muted-foreground">Units</label>
                      <input type="number" min="1" className="w-full border rounded px-2 py-1 text-xs text-center"
                        value={code.units} onChange={e => updateCode(idx, 'units', parseInt(e.target.value) || 1)}
                        data-testid={`units-input-${idx}`} />
                    </div>
                    {/* Charge */}
                    <div className="w-20">
                      <label className="text-[9px] text-muted-foreground">Charge</label>
                      <input type="number" className="w-full border rounded px-2 py-1 text-xs"
                        value={code.charge_amount || ''} onChange={e => updateCode(idx, 'charge_amount', parseFloat(e.target.value) || 0)}
                        placeholder="0" data-testid={`charge-input-${idx}`} />
                    </div>
                    {/* Remove */}
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 hover:text-red-600 mt-3"
                      onClick={() => removeCode(idx)} data-testid={`remove-code-${idx}`}>
                      <XCircle className="h-3.5 w-3.5" />
                    </Button>
                  </div>

                  {/* Modifiers */}
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className="text-[9px] text-muted-foreground mr-1">Modifiers:</span>
                    {(code.modifiers || []).map((m, mi) => (
                      <span key={mi} className="inline-flex items-center gap-0.5 text-[10px] bg-blue-100 text-blue-700 rounded px-1.5 py-0.5">
                        {m}
                        <button onClick={() => removeModifier(idx, mi)} className="text-blue-400 hover:text-red-600 ml-0.5">&times;</button>
                      </span>
                    ))}
                    <select className="text-[10px] border rounded px-1 py-0.5 bg-white" defaultValue=""
                      onChange={e => { addModifier(idx, e.target.value); e.target.value = ''; }}
                      data-testid={`modifier-select-${idx}`}>
                      <option value="">+mod</option>
                      {['-25','-59','-76','-77','-50','-LT','-RT','-26','-TC','-GP','-GN','-GO'].map(m => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                  </div>

                  {/* Diagnosis Linkage (structured, from encounter) */}
                  {diagnoses.length > 0 && (
                    <div className="flex items-start gap-1 flex-wrap">
                      <span className="text-[9px] text-muted-foreground mr-1 mt-0.5">Dx Link:</span>
                      {diagnoses.map((dx) => {
                        const linked = (code.linked_diagnoses || []).includes(dx.code);
                        return (
                          <button key={dx.code}
                            className={`text-[9px] rounded px-1.5 py-0.5 border transition-colors ${linked ? 'bg-emerald-100 border-emerald-300 text-emerald-700 font-medium' : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'}`}
                            onClick={() => toggleDiagnosis(idx, dx.code)}
                            data-testid={`dx-link-${idx}-${dx.code}`}>
                            {dx.code}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
              {editCodes.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-3">No codes. Click "+ Add Code" to begin.</p>
              )}
            </div>
          </div>

          {/* Coder Rationale Box (required field) */}
          <div data-testid="rationale-section">
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">
              Coder Rationale <span className="text-red-500">*</span>
            </p>
            <textarea className="w-full border rounded p-2 text-xs min-h-[60px]" rows={3}
              placeholder="Why these codes are correct — required for approval..."
              value={rationale} onChange={e => { setRationale(e.target.value); setDirty(true); }}
              data-testid="rationale-input" />
          </div>

          {/* ═══ SAVE ACTIONS (Slice C) ═══ */}
          <div className="space-y-2 pt-2 border-t" data-testid="save-actions">
            {/* Release to Billing — shown ONLY when approved and not yet released */}
            {data?.charge?.coding_state === 'approved' && data?.charge?.charge_status !== 'ready_for_claim' && (
              <Button size="sm" className="h-8 text-xs w-full bg-blue-600 hover:bg-blue-700 text-white"
                onClick={releaseToBilling} disabled={saving} data-testid="release-to-billing-btn">
                {saving ? 'Releasing...' : 'Release to Billing — Create Claim'}
              </Button>
            )}
            {data?.charge?.charge_status === 'ready_for_claim' && (
              <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-xs text-emerald-700 text-center" data-testid="already-released">
                Released to billing — claim exists
              </div>
            )}
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" className="h-7 text-xs" variant="outline" onClick={saveDraft} disabled={saving} data-testid="save-draft-btn">
                {saving ? <><RotateCw className="h-3 w-3 mr-1 animate-spin" />Saving...</> : <><FileText className="h-3 w-3 mr-1" />Save Draft</>}
              </Button>
              <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={approveCoding} disabled={saving} data-testid="approve-btn">
                <CheckCircle2 className="h-3 w-3 mr-1" />Approve Coding
              </Button>
              <Button size="sm" className="h-7 text-xs" variant="outline" onClick={() => setShowFlagDialog(true)} disabled={saving} data-testid="flag-review-btn">
                <Eye className="h-3 w-3 mr-1" />Flag for Review
              </Button>
              <Button size="sm" className="h-7 text-xs" variant="outline" onClick={() => setShowReturnDialog(true)} disabled={saving} data-testid="return-provider-btn">
                <Send className="h-3 w-3 mr-1" />Send Back to Provider
              </Button>
            </div>

            {/* Flag for Review dialog */}
            {showFlagDialog && (
              <div className="border rounded p-2 bg-amber-50 space-y-1.5" data-testid="flag-dialog">
                <p className="text-[10px] font-medium text-amber-700">Why does this need review?</p>
                <textarea className="w-full border rounded p-1.5 text-xs" rows={2}
                  placeholder="Reason for flagging..." value={flagReason}
                  onChange={e => setFlagReason(e.target.value)} data-testid="flag-reason-input" />
                <div className="flex gap-1.5">
                  <Button size="sm" className="h-6 text-[10px]" onClick={flagForReview}>Confirm Flag</Button>
                  <Button size="sm" className="h-6 text-[10px]" variant="ghost" onClick={() => setShowFlagDialog(false)}>Cancel</Button>
                </div>
              </div>
            )}

            {/* Return to Provider dialog */}
            {showReturnDialog && (
              <div className="border rounded p-2 bg-gray-50 space-y-1.5" data-testid="return-dialog">
                <p className="text-[10px] font-medium">Message to provider <span className="text-red-500">*</span></p>
                <textarea className="w-full border rounded p-1.5 text-xs" rows={2}
                  placeholder="What does the provider need to clarify or address?"
                  value={returnMsg} onChange={e => setReturnMsg(e.target.value)} data-testid="return-message-input" />
                <div className="flex gap-1.5">
                  <Button size="sm" className="h-6 text-[10px]" onClick={returnToProvider}>Send to Provider</Button>
                  <Button size="sm" className="h-6 text-[10px]" variant="ghost" onClick={() => setShowReturnDialog(false)}>Cancel</Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

// ═══════════════════════════════════════
// MAIN CODING WORKSPACE PAGE — Vertical Split Layout
// Top: Queue (collapsible). Bottom: Workbench (dominant).
// Draggable divider between panes.
// ═══════════════════════════════════════
const QUEUE_DEFAULT_HEIGHT = 320;
const QUEUE_COLLAPSED_HEIGHT = 52;
const QUEUE_MIN = 52;
const QUEUE_MAX = 600;

export default function CodingWorkspace() {
  const [tab, setTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [selectedCharge, setSelectedCharge] = useState(null);
  const [queueHeight, setQueueHeight] = useState(QUEUE_DEFAULT_HEIGHT);
  const [collapsed, setCollapsed] = useState(false);
  const dividerRef = useRef(null);
  const dragging = useRef(false);
  const startY = useRef(0);
  const startH = useRef(0);

  const loadDashboard = useCallback(async () => {
    try {
      const r = await api.get('/rcm/coding/dashboard');
      setDashboard(r.data);
    } catch(e) { console.error(e); }
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const handleSelectBucket = (state) => { setTab(state); setCollapsed(false); setQueueHeight(QUEUE_DEFAULT_HEIGHT); };

  const handleSelectCharge = (chargeId) => {
    setSelectedCharge(chargeId);
    // Auto-collapse queue when patient selected
    setCollapsed(true);
    setQueueHeight(QUEUE_COLLAPSED_HEIGHT);
  };

  const handleBucketChange = (newTab) => {
    setTab(newTab);
    // Reset to default expanded when switching buckets
    if (newTab === 'dashboard') {
      setSelectedCharge(null);
      setCollapsed(false);
      setQueueHeight(QUEUE_DEFAULT_HEIGHT);
    } else {
      setCollapsed(false);
      setQueueHeight(QUEUE_DEFAULT_HEIGHT);
    }
  };

  const toggleCollapse = () => {
    if (collapsed) {
      setCollapsed(false);
      setQueueHeight(QUEUE_DEFAULT_HEIGHT);
    } else {
      setCollapsed(true);
      setQueueHeight(QUEUE_COLLAPSED_HEIGHT);
    }
  };

  // Draggable divider
  const onDividerMouseDown = (e) => {
    e.preventDefault();
    dragging.current = true;
    startY.current = e.clientY;
    startH.current = queueHeight;
    document.addEventListener('mousemove', onDividerMouseMove);
    document.addEventListener('mouseup', onDividerMouseUp);
  };

  const onDividerMouseMove = useCallback((e) => {
    if (!dragging.current) return;
    const delta = e.clientY - startY.current;
    const newH = Math.min(QUEUE_MAX, Math.max(QUEUE_MIN, startH.current + delta));
    setQueueHeight(newH);
    setCollapsed(newH <= QUEUE_MIN + 10);
  }, []);

  const onDividerMouseUp = useCallback(() => {
    dragging.current = false;
    document.removeEventListener('mousemove', onDividerMouseMove);
    document.removeEventListener('mouseup', onDividerMouseUp);
  }, [onDividerMouseMove]);

  const showWorkbench = selectedCharge && tab !== 'dashboard';

  return (
    <div className="p-4 md:p-6 max-w-[1440px] flex flex-col" style={{ height: 'calc(100vh - 64px)' }} data-testid="coding-workspace">
      {/* Header — always visible, compact */}
      <div className="flex items-center justify-between mb-2 shrink-0">
        <div>
          <h1 className="text-lg font-bold tracking-tight" data-testid="coding-workspace-title">Coding Workspace</h1>
          <p className="text-[10px] text-muted-foreground">workflow-first, audit-trail-first</p>
        </div>
      </div>

      {/* Tabs — always visible */}
      <Tabs value={tab} onValueChange={handleBucketChange} className="shrink-0" data-testid="coding-tabs">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="dashboard" data-testid="coding-tab-dashboard">
            <FileText className="h-3 w-3 mr-1.5" />Dashboard
          </TabsTrigger>
          <TabsTrigger value="ready" data-testid="coding-tab-ready" className="text-emerald-700">
            <CheckCircle2 className="h-3 w-3 mr-1.5" />Ready
            {dashboard?.buckets?.ready?.count > 0 && <Badge variant="secondary" className="ml-1.5 text-[9px] h-4 px-1">{dashboard.buckets.ready.count}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="needs_review" data-testid="coding-tab-needs-review">
            <Eye className="h-3 w-3 mr-1.5 text-amber-600" />Review
            {dashboard?.buckets?.needs_review?.count > 0 && <Badge className="ml-1.5 text-[9px] h-4 px-1 bg-amber-100 text-amber-700">{dashboard.buckets.needs_review.count}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="needs_fix" data-testid="coding-tab-needs-fix">
            <XCircle className="h-3 w-3 mr-1.5 text-red-600" />Fix
            {dashboard?.buckets?.needs_fix?.count > 0 && <Badge variant="destructive" className="ml-1.5 text-[9px] h-4 px-1">{dashboard.buckets.needs_fix.count}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="in_progress" data-testid="coding-tab-in-progress">
            <Clock className="h-3 w-3 mr-1.5 text-blue-600" />In Progress
            {dashboard?.buckets?.in_progress?.count > 0 && <Badge variant="outline" className="ml-1.5 text-[9px] h-4 px-1">{dashboard.buckets.in_progress.count}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="returned_to_provider" data-testid="coding-tab-returned">
            <Send className="h-3 w-3 mr-1.5 text-gray-500" />Returned
            {dashboard?.buckets?.returned_to_provider?.count > 0 && <Badge variant="outline" className="ml-1.5 text-[9px] h-4 px-1">{dashboard.buckets.returned_to_provider.count}</Badge>}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Vertical split: Queue (top) + Workbench (bottom) */}
      <div className="flex-1 flex flex-col min-h-0 mt-2">
        {/* ═══ TOP PANE: Queue (resizable/collapsible) ═══ */}
        <div
          className="shrink-0 overflow-hidden transition-[height] duration-200 ease-out"
          style={{ height: tab === 'dashboard' ? 'auto' : `${queueHeight}px` }}
          data-testid="queue-pane"
        >
          <div className="h-full overflow-y-auto">
            {tab === 'dashboard' ? (
              <DashboardTab dashboard={dashboard} onSelectBucket={handleSelectBucket} />
            ) : (
              <BucketTab state={tab} onSelectCharge={handleSelectCharge} selectedId={selectedCharge} />
            )}
          </div>
        </div>

        {/* ═══ DRAGGABLE DIVIDER ═══ */}
        {showWorkbench && (
          <div
            ref={dividerRef}
            className="shrink-0 flex items-center justify-center h-5 cursor-row-resize group hover:bg-muted/50 border-y border-transparent hover:border-border transition-colors select-none"
            onMouseDown={onDividerMouseDown}
            data-testid="split-divider"
          >
            <div className="flex items-center gap-2">
              <button onClick={toggleCollapse} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="toggle-collapse">
                {collapsed ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
              </button>
              <GripHorizontal className="h-3 w-3 text-muted-foreground/50 group-hover:text-muted-foreground" />
              <span className="text-[9px] text-muted-foreground">{collapsed ? 'Expand queue' : 'Drag to resize'}</span>
            </div>
          </div>
        )}

        {/* ═══ BOTTOM PANE: Workbench (dominant) ═══ */}
        {showWorkbench && (
          <div className="flex-1 min-h-0 overflow-y-auto" data-testid="workbench-pane">
            <WorkbenchPanel chargeId={selectedCharge} onClose={() => { setSelectedCharge(null); setCollapsed(false); setQueueHeight(QUEUE_DEFAULT_HEIGHT); loadDashboard(); }} />
          </div>
        )}
      </div>
    </div>
  );
}
